import { Assistant, QuestionAnalysis } from '../types';
import { generateSystemPrompt, generateQuestionAnalysisPrompt, generateContextCardsPrompt, generatePersonalContextCategoriesPrompt } from '../utils/prompts';

import { OPENAI_SERVER_URL, PROJECT_NAME } from '../constants';

export const generateAssistantResponse = async (
    userMessage: string,
    assistant: Assistant
): Promise<string> => {
    try {
        const systemPrompt = await generateSystemPrompt(assistant);

        const response = await fetch(`${OPENAI_SERVER_URL}/api/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-project-name': PROJECT_NAME,
            },
            body: JSON.stringify({
                model: 'gpt-4o-mini',
                messages: [
                    {
                        role: 'system',
                        content: systemPrompt,
                    },
                    {
                        role: 'user',
                        content: userMessage,
                    },
                ],
                max_tokens: 1000,
                temperature: 0.7,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.choices && data.choices.length > 0) {
            return data.choices[0].message.content;
        } else {
            throw new Error('No response content received from OpenAI');
        }
    } catch (error) {
        console.error('Error generating response:', error);
        throw new Error('Failed to generate response');
    }
};

export const analyzeQuestionsWithAI = async (
    questions: string[],
    assistantName: string
): Promise<QuestionAnalysis> => {
    try {
        const prompt = generateQuestionAnalysisPrompt(questions, assistantName);

        const response = await fetch(`${OPENAI_SERVER_URL}/api/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-project-name': PROJECT_NAME,
            },
            body: JSON.stringify({
                model: 'gpt-4o-mini',
                messages: [
                    {
                        role: 'system',
                        content: 'You are an expert business analyst. Analyze the given questions and provide insights in the exact JSON format requested. Be precise and business-focused.',
                    },
                    {
                        role: 'user',
                        content: prompt,
                    },
                ],
                max_tokens: 1500,
                temperature: 0.3,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const aiResponse = data.choices?.[0]?.message?.content;

        if (!aiResponse) {
            throw new Error('No response content received from OpenAI');
        }

        // Try to parse the JSON response
        try {
            const analysis = JSON.parse(aiResponse);

            // Validate the structure
            if (!analysis.categories || !analysis.commonThemes || !analysis.insights) {
                throw new Error('Invalid response structure from AI');
            }

            return analysis as QuestionAnalysis;
        } catch (parseError) {
            console.error('Error parsing AI response:', parseError);
            console.log('Raw AI response:', aiResponse);

            // Fallback to basic analysis if AI parsing fails
            return {
                categories: [{
                    category: "General Inquiries",
                    count: questions.length,
                    examples: questions.slice(0, 3)
                }],
                commonThemes: ["Various topics"],
                insights: ["Visitors are asking questions about your services and expertise"]
            };
        }
    } catch (error) {
        console.error('Error analyzing questions with AI:', error);

        // Fallback analysis for when AI fails
        return {
            categories: [{
                category: "General Inquiries",
                count: questions.length,
                examples: questions.slice(0, 3)
            }],
            commonThemes: ["Various topics"],
            insights: ["Visitors are asking questions about your services and expertise"]
        };
    }
};

// Context card interface and main logic
export interface ContextCard {
    id: string;
    icon: string;
    emoji: string;
    title: string;
    prompt: string;
}

export const generateContextCardsWithAI = async (assistant: Assistant): Promise<ContextCard[]> => {
    try {
        console.log('Generating context cards for assistant:', assistant.name);
        const prompt = await generateContextCardsPrompt(assistant);
        console.log('Generated prompt:', prompt);

        // Check if we have any context content
        if (!prompt.includes('About') || prompt.includes('About :')) {
            console.warn('No context content found, using fallback cards');
            return generateContextCards(assistant);
        }

        console.log('Making OpenAI API request for context cards...');

        // Check if OpenAI server is available
        try {
            const healthCheck = await fetch(`${OPENAI_SERVER_URL}/health`);
            if (!healthCheck.ok) {
                console.warn('OpenAI server not available, using fallback cards');
                return generateContextCards(assistant);
            }
        } catch (error) {
            console.warn('OpenAI server health check failed, using fallback cards:', error);
            return generateContextCards(assistant);
        }

        const response = await fetch(`${OPENAI_SERVER_URL}/api/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-project-name': PROJECT_NAME,
            },
            body: JSON.stringify({
                model: 'gpt-4o-mini',
                messages: [
                    {
                        role: 'system',
                        content: 'You are an expert at creating engaging conversation topics. Generate interesting topics based on the provided context in the exact JSON format requested.',
                    },
                    {
                        role: 'user',
                        content: prompt,
                    },
                ],
                max_tokens: 800,
                temperature: 0.7,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const aiResponse = data.choices?.[0]?.message?.content;

        if (!aiResponse) {
            throw new Error('No response content received from OpenAI');
        }

        // Try to parse the JSON response
        try {
            const result = JSON.parse(aiResponse);

            if (!result.topics || !Array.isArray(result.topics)) {
                throw new Error('Invalid response structure from AI');
            }

            console.log('Parsed AI response:', result);

            // Map AI-generated topics to ContextCard format with icons
            const contextCards: ContextCard[] = result.topics.map((topic: any, index: number) => ({
                id: topic.id || `topic_${index}`,
                icon: getIconForTopic(topic.title),
                emoji: topic.emoji || getEmojiForTopic(topic.title),
                title: topic.title,
                prompt: topic.prompt
            }));

            // Add predefined categories
            const predefinedCards: ContextCard[] = [
                {
                    id: 'fun_fact',
                    icon: 'TvIcon',
                    emoji: '📺',
                    title: 'Fun fact',
                    prompt: `Tell me something interesting or fun about ${assistant.name} that people might not know.`
                },
                {
                    id: 'contact',
                    icon: 'MessageSquare',
                    emoji: '💬',
                    title: 'Contact',
                    prompt: `How can I get in touch with ${assistant.name} or learn more about working with ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
                },
                {
                    id: 'whats_echoo',
                    icon: 'HelpCircle',
                    emoji: '❓',
                    title: `${assistant.name.split(' ')[0]}'s echoo?`,
                    prompt: `What is ${assistant.name}'s echoo and how can it help me learn about ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
                }
            ];

            // Combine AI-generated topics with predefined ones (6 AI + 3 predefined = 9 total)
            return [...contextCards, ...predefinedCards];

        } catch (parseError) {
            console.error('Error parsing AI response:', parseError);
            console.log('Raw AI response:', aiResponse);

            // Fallback to predefined cards if AI parsing fails
            return [
                {
                    id: 'fun_fact',
                    icon: 'TvIcon',
                    emoji: '📺',
                    title: 'Fun fact',
                    prompt: `Tell me something interesting or fun about ${assistant.name} that people might not know.`
                },
                {
                    id: 'contact',
                    icon: 'MessageSquare',
                    emoji: '💬',
                    title: 'Contact',
                    prompt: `How can I get in touch with ${assistant.name} or learn more about working with ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
                },
                {
                    id: 'whats_echoo',
                    icon: 'HelpCircle',
                    emoji: '❓',
                    title: `${assistant.name.split(' ')[0]}'s echoo?`,
                    prompt: `What is ${assistant.name}'s echoo and how can it help me learn about ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
                }
            ];
        }
    } catch (error) {
        console.error('Error generating context cards with AI:', error);

        // Fallback to predefined cards
        return [
            {
                id: 'fun_fact',
                icon: 'TvIcon',
                emoji: '📺',
                title: 'Fun fact',
                prompt: `Tell me something interesting or fun about ${assistant.name} that people might not know.`
            },
            {
                id: 'contact',
                icon: 'MessageSquare',
                emoji: '💬',
                title: 'Contact',
                prompt: `How can I get in touch with ${assistant.name} or learn more about working with ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
            },
            {
                id: 'whats_echoo',
                icon: 'HelpCircle',
                emoji: '❓',
                title: `${assistant.name.split(' ')[0]}'s echoo?`,
                prompt: `What is ${assistant.name}'s echoo and how can it help me learn about ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
            }
        ];
    }
};

// Helper function to assign icons based on topic content
const getIconForTopic = (title: string): string => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('work') || lowerTitle.includes('experience') || lowerTitle.includes('career')) {
        return 'Briefcase';
    } else if (lowerTitle.includes('personality') || lowerTitle.includes('personal') || lowerTitle.includes('who')) {
        return 'Smile';
    } else if (lowerTitle.includes('testimonial') || lowerTitle.includes('feedback') || lowerTitle.includes('review')) {
        return 'Zap';
    } else if (lowerTitle.includes('fun') || lowerTitle.includes('interesting') || lowerTitle.includes('fact')) {
        return 'TvIcon';
    } else if (lowerTitle.includes('contact') || lowerTitle.includes('reach') || lowerTitle.includes('connect')) {
        return 'MessageSquare';
    } else {
        return 'HelpCircle'; // Default icon
    }
};

// Helper function to assign emojis based on topic content
const getEmojiForTopic = (title: string): string => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('work') || lowerTitle.includes('experience') || lowerTitle.includes('career')) {
        return '💼';
    } else if (lowerTitle.includes('personality') || lowerTitle.includes('personal') || lowerTitle.includes('who')) {
        return '😊';
    } else if (lowerTitle.includes('testimonial') || lowerTitle.includes('feedback') || lowerTitle.includes('review')) {
        return '⚡';
    } else if (lowerTitle.includes('fun') || lowerTitle.includes('interesting') || lowerTitle.includes('fact')) {
        return '📺';
    } else if (lowerTitle.includes('contact') || lowerTitle.includes('reach') || lowerTitle.includes('connect')) {
        return '💬';
    } else if (lowerTitle.includes('project')) {
        return '🚀';
    } else if (lowerTitle.includes('philosophy') || lowerTitle.includes('approach')) {
        return '💭';
    } else if (lowerTitle.includes('highlight') || lowerTitle.includes('achievement')) {
        return '⭐';
    } else if (lowerTitle.includes('interest') || lowerTitle.includes('hobby')) {
        return '🎨';
    } else if (lowerTitle.includes('insight') || lowerTitle.includes('advice')) {
        return '💡';
    } else if (lowerTitle.includes('collaboration') || lowerTitle.includes('team')) {
        return '🤝';
    } else {
        return '❓'; // Default emoji
    }
};

// Fallback function for when AI is not available
export const generateContextCards = (assistant: Assistant): ContextCard[] => {
    return [
        {
            id: 'fun_fact',
            icon: 'TvIcon',
            emoji: '📺',
            title: 'Fun fact',
            prompt: `Tell me something interesting or fun about ${assistant.name} that people might not know.`
        },
        {
            id: 'contact',
            icon: 'MessageSquare',
            emoji: '💬',
            title: 'Contact',
            prompt: `How can I get in touch with ${assistant.name} or learn more about working with ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
        },
        {
            id: 'whats_echoo',
            icon: 'HelpCircle',
            emoji: '❓',
            title: `${assistant.name.split(' ')[0]}'s echoo?`,
            prompt: `What is ${assistant.name}'s echoo and how can it help me learn about ${assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'}?`
        }
    ];
};

// Interface for personal context categories
export interface PersonalContextCategory {
    title: string;
    description: string;
    prompt: string;
}

// Function to generate personal context categories using OpenAI
export const generatePersonalContextCategories = async (
    name: string,
    learningObjective: string
): Promise<PersonalContextCategory[]> => {
    try {
        console.log('Generating personal context categories for:', name, learningObjective);

        const prompt = generatePersonalContextCategoriesPrompt(name, learningObjective);

        // Check if OpenAI server is available
        try {
            const healthCheck = await fetch(`${OPENAI_SERVER_URL}/health`);
            if (!healthCheck.ok) {
                console.warn('OpenAI server not available, using fallback categories');
                return generateFallbackCategories(name, learningObjective);
            }
        } catch (error) {
            console.warn('OpenAI server health check failed, using fallback categories:', error);
            return generateFallbackCategories(name, learningObjective);
        }

        const response = await fetch(`${OPENAI_SERVER_URL}/api/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-project-name': PROJECT_NAME,
            },
            body: JSON.stringify({
                model: 'gpt-4o-mini',
                messages: [
                    {
                        role: 'system',
                        content: 'You are an expert at creating engaging personal context categories. Generate exactly 4 categories based on the provided information. Return ONLY a valid JSON array with no markdown formatting or additional text.',
                    },
                    {
                        role: 'user',
                        content: prompt,
                    },
                ],
                max_tokens: 800,
                temperature: 0.7,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const aiResponse = data.choices?.[0]?.message?.content;

        if (!aiResponse) {
            throw new Error('No response content received from OpenAI');
        }

        // Try to parse the JSON response
        try {
            // Clean the response to remove markdown code blocks if present
            let cleanedResponse = aiResponse.trim();
            if (cleanedResponse.startsWith('```json')) {
                cleanedResponse = cleanedResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
            } else if (cleanedResponse.startsWith('```')) {
                cleanedResponse = cleanedResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
            }

            const result = JSON.parse(cleanedResponse);

            if (!Array.isArray(result) || result.length !== 4) {
                throw new Error('Invalid response structure from AI - expected array of 4 categories');
            }

            // Validate each category has required fields
            const categories: PersonalContextCategory[] = result.map((category: any, index: number) => {
                if (!category.title || !category.description || !category.prompt) {
                    throw new Error(`Category ${index + 1} missing required fields`);
                }
                return {
                    title: category.title,
                    description: category.description,
                    prompt: category.prompt
                };
            });

            console.log('Generated categories:', categories);
            return categories;

        } catch (parseError) {
            console.error('Error parsing AI response:', parseError);
            console.log('Raw AI response:', aiResponse);

            // Fallback to predefined categories if AI parsing fails
            return generateFallbackCategories(name, learningObjective);
        }
    } catch (error) {
        console.error('Error generating personal context categories:', error);

        // Fallback to predefined categories
        return generateFallbackCategories(name, learningObjective);
    }
};

// Fallback function for when AI is not available
const generateFallbackCategories = (name: string, learningObjective: string): PersonalContextCategory[] => {
    // Analyze the learning objective to provide relevant categories
    const lowerObjective = learningObjective.toLowerCase();

    if (lowerObjective.includes('professional') || lowerObjective.includes('work') || lowerObjective.includes('expertise')) {
        return [
            {
                title: "Professional Background",
                description: "Your career journey, experience, and professional achievements",
                prompt: `Tell me about your professional background, including your career path, key experiences, and what you've accomplished in your field.`
            },
            {
                title: "Skills & Expertise",
                description: "Your core competencies, technical skills, and areas of specialization",
                prompt: `What are your key skills and areas of expertise? What makes you uniquely qualified in your field?`
            },
            {
                title: "Key Achievements",
                description: "Notable accomplishments, awards, and career highlights",
                prompt: `Share some of your most significant achievements, awards, or career milestones that you're proud of.`
            },
            {
                title: "Professional Philosophy",
                description: "Your approach to work, values, and what drives you professionally",
                prompt: `What's your professional philosophy? What values guide your work and how do you approach challenges?`
            }
        ];
    } else if (lowerObjective.includes('personal') || lowerObjective.includes('story') || lowerObjective.includes('memory')) {
        return [
            {
                title: "Personal Journey",
                description: "Your life story, background, and the experiences that shaped you",
                prompt: `Share your personal journey - the experiences, people, and moments that have shaped who you are today.`
            },
            {
                title: "Life Experiences",
                description: "Memorable moments, challenges overcome, and lessons learned",
                prompt: `What are some of the most significant life experiences you've had? What have you learned from them?`
            },
            {
                title: "Values & Beliefs",
                description: "What matters most to you and the principles you live by",
                prompt: `What are your core values and beliefs? What principles guide your decisions and actions?`
            },
            {
                title: "Personal Interests",
                description: "Your hobbies, passions, and what brings you joy outside of work",
                prompt: `What are your personal interests and hobbies? What activities bring you joy and fulfillment?`
            }
        ];
    } else if (lowerObjective.includes('artistic') || lowerObjective.includes('creative') || lowerObjective.includes('inspiration')) {
        return [
            {
                title: "Creative Process",
                description: "How you approach your creative work and the methods you use",
                prompt: `Walk me through your creative process. How do you approach your artistic work and what methods do you use?`
            },
            {
                title: "Inspiration Sources",
                description: "What inspires your creativity and where you find new ideas",
                prompt: `What inspires your creativity? Where do you find new ideas and what influences your artistic vision?`
            },
            {
                title: "Portfolio Highlights",
                description: "Your best work, favorite pieces, and what makes them special",
                prompt: `Share some highlights from your portfolio. What are your favorite pieces and what makes them special to you?`
            },
            {
                title: "Artistic Vision",
                description: "Your creative philosophy and what you hope to achieve through your art",
                prompt: `What's your artistic vision? What do you hope to achieve through your creative work?`
            }
        ];
    } else {
        // Generic categories for any other learning objective
        return [
            {
                title: "Background & Experience",
                description: "Your journey, background, and key experiences",
                prompt: `Tell me about your background and the experiences that have shaped who you are.`
            },
            {
                title: "Key Insights",
                description: "Important lessons learned and insights gained",
                prompt: `What are some key insights or lessons you've learned that you'd like to share with others?`
            },
            {
                title: "What Makes You Unique",
                description: "Your unique qualities, perspectives, and what sets you apart",
                prompt: `What makes you unique? What perspectives or qualities do you bring that others might find valuable?`
            },
            {
                title: "Goals & Aspirations",
                description: "What you're working towards and what you hope to achieve",
                prompt: `What are your current goals and aspirations? What are you working towards?`
            }
        ];
    }
};
